/**
 * fb-model.ts — Flow builder constants, step factories, and tree helpers.
 * Ported verbatim from flow-builder-model.jsx + flow-builder-layout.jsx.
 */
import { T } from "@v0y4ge/jrni-ui";
import { t as i18nT } from "../../lib/i18n";

export const FB_NODE_W = 272;
export const FB_ACCENT = T.p1;
export const FB_GAP_X = 46, FB_SLOT = 50, FB_DGAP = 62, FB_ENDH = 34, FB_TRIG_H = 96;

export interface FBTrigger { id: string; label: string; labelKey: string; sub: string; }
export const FB_TRIGGERS: FBTrigger[] = [
  { id: "invited",    labelKey: "builder.fbModel.guestIsInvited", get label() { return i18nT("builder.fbModel.guestIsInvited"); },       sub: "Flow starts when a guest is added / invited" },
  { id: "registered", labelKey: "builder.fbModel.guestRegisters", get label() { return i18nT("builder.fbModel.guestRegisters"); },        sub: "Flow starts when a guest registers or RSVPs yes" },
  { id: "declined",   labelKey: "builder.fbModel.guestDeclines", get label() { return i18nT("builder.fbModel.guestDeclines"); },         sub: "Flow starts when a guest declines or RSVPs no" },
  { id: "waitlisted", labelKey: "builder.fbModel.guestJoinsWaitlist", get label() { return i18nT("builder.fbModel.guestJoinsWaitlist"); },   sub: "Flow starts when a guest is waitlisted" },
  { id: "published",  labelKey: "builder.fbModel.eventIsLive", get label() { return i18nT("builder.fbModel.eventIsLive"); },          sub: "Flow starts when the event goes live" },
  { id: "joined_queue", labelKey: "builder.fbModel.customerJoinsTheQueue", get label() { return i18nT("builder.fbModel.customerJoinsTheQueue", "Customer joins the queue"); }, sub: "Flow starts when a customer takes a place in a queue" },
  { id: "booked",     labelKey: "builder.fbModel.customerBooksAnAppointment", get label() { return i18nT("builder.fbModel.customerBooksAnAppointment", "Customer books an appointment"); }, sub: "Flow starts when an appointment is booked" },
  { id: "requested",  labelKey: "builder.fbModel.customerRequestsPrivateAccess", get label() { return i18nT("builder.fbModel.customerRequestsPrivateAccess", "Customer requests private access"); }, sub: "Flow starts when someone requests a private service or event" },
];
export const FB_TRIGGER = (id: string) => FB_TRIGGERS.find((t) => t.id === id) ?? FB_TRIGGERS[0];

export const FB_DECISION_CONDS = [
  "Registered within 2 days","Responded positively","Accepted the invitation",
  "Opened the message","Clicked the link","Confirmed attendance","Attended the event","Made a purchase",
  "Responded to the invitation","Attended the appointment","Confirmed the appointment",
  "Was served","Still in the queue","Got a seat (not waitlisted)","Approved",
];
export const FB_EXIT_OUTCOMES = ["Declined","Completed the flow","Goal met","No response","Unsubscribed","Bounced"];
export const FB_WAIT_UNITS = ["Minutes","Hours","Days"];
export const FB_WAIT_TIMINGS = [
  { value:"after_prev",    labelKey: "builder.fbModel.afterThePreviousStep", get label() { return i18nT("builder.fbModel.afterThePreviousStep"); } },
  { value:"before_event",  labelKey: "builder.fbModel.beforeTheEvent", get label() { return i18nT("builder.fbModel.beforeTheEvent"); } },
  { value:"after_event",   labelKey: "builder.fbModel.afterTheEvent", get label() { return i18nT("builder.fbModel.afterTheEvent"); } },
];
export const FB_SENDWHEN = ["Immediately","After previous step","Scheduled date"];

let _fbSeq = 0;
const fbId = (p: string) => `${p}_${Date.now().toString(36)}${(_fbSeq++).toString(36)}${Math.random().toString(36).slice(2,4)}`;

export interface StepComms  { id:string; kind:"comms";    messageId:string|null; sendWhen:string; landingId:string; }
export interface StepWait   { id:string; kind:"wait";     amount:number; unit:string; timing:string; }
export interface StepDecision{ id:string; kind:"decision"; condition:string; yes:FBStep[]; no:FBStep[]; }
export interface StepLanding{ id:string; kind:"landing";  landingId:string; }
export interface StepSurvey { id:string; kind:"survey";   surveyId:string|null; sendWhen:string; }
export interface StepExit   { id:string; kind:"exit";     outcome:string; note:string; }
export type FBStep = StepComms|StepWait|StepDecision|StepLanding|StepSurvey|StepExit;

export interface FBFlow { trigger:{ event:string }; steps:FBStep[]; }

export const fbMkComms   = (messageId:string|null=null): StepComms    => ({ id:fbId("c"), kind:"comms",    messageId, sendWhen:"Immediately", landingId:"" });
export const fbMkWait    = (): StepWait    => ({ id:fbId("w"), kind:"wait",    amount:1, unit:"Days", timing:"after_prev" });
export const fbMkDecision= (): StepDecision=> ({ id:fbId("d"), kind:"decision", condition:"Registered within 2 days", yes:[], no:[] });
export const fbMkLanding = (): StepLanding => ({ id:fbId("lp"), kind:"landing", landingId:"" });
export const fbMkSurvey  = (surveyId:string|null=null): StepSurvey => ({ id:fbId("sv"), kind:"survey", surveyId, sendWhen:"Immediately" });
export const fbMkExit    = (): StepExit    => ({ id:fbId("x"), kind:"exit",    outcome:"Declined", note:"" });

export function fbDefaultFlow(): FBFlow {
  return { trigger:{ event:"invited" }, steps:[fbMkComms(), fbMkDecision()] };
}

export function fbFindStep(steps: FBStep[], id: string): FBStep|null {
  for (const s of steps) {
    if (s.id===id) return s;
    if (s.kind==="decision") { const r = fbFindStep(s.yes,id) || fbFindStep(s.no,id); if (r) return r; }
  }
  return null;
}

export function fbEdit(steps: FBStep[], id: string, fn: (s:FBStep)=>FBStep): FBStep[] {
  return steps.map((s) => {
    if (s.id===id) return fn(s);
    if (s.kind==="decision") return { ...s, yes:fbEdit(s.yes,id,fn), no:fbEdit(s.no,id,fn) };
    return s;
  });
}

export function fbRemove(steps: FBStep[], id: string): FBStep[] {
  return steps.filter((s) => s.id!==id).map((s) =>
    s.kind==="decision" ? { ...s, yes:fbRemove(s.yes,id), no:fbRemove(s.no,id) } : s
  );
}

export interface InsertDesc { parentId:string|null; branch:"yes"|"no"|null; index:number; }
export function fbInsert(steps: FBStep[], desc: InsertDesc, step: FBStep): FBStep[] {
  if (!desc.parentId) { const n = steps.slice(); n.splice(desc.index,0,step); return n; }
  return steps.map((s) => {
    if (s.id===desc.parentId && s.kind==="decision") {
      const arr = (desc.branch==="yes" ? s.yes : s.no).slice();
      arr.splice(desc.index,0,step);
      return desc.branch==="yes" ? { ...s, yes:arr } : { ...s, no:arr };
    }
    if (s.kind==="decision") return { ...s, yes:fbInsert(s.yes,desc,step), no:fbInsert(s.no,desc,step) };
    return s;
  });
}

export const fbBranchLabels = (_cond: string): [string,string] => ["Yes","No"];

/** Human label for a wait step — timing-aware so the card reads naturally.
 *  before_event → "7 days before the event"; after_event → "… after the event";
 *  after_prev (default) → "Wait 7 days". */
export function fbWaitLabel(s: StepWait): string {
  const unit = String(s.unit || "").toLowerCase();
  const qty = `${s.amount} ${unit}`;
  if (s.timing === "before_event") return `${qty} before the event`;
  if (s.timing === "after_event") return `${qty} after the event`;
  return `Wait ${qty}`;
}

export function fbNodeHeight(s: FBStep|null): number {
  if (!s) return 96;
  if (s.kind==="comms")    return 92 + (s.landingId?30:0);
  if (s.kind==="wait")     return 60;
  if (s.kind==="survey")   return 92;
  if (s.kind==="decision") return 80;
  if (s.kind==="landing")  return 90;
  if (s.kind==="exit")     return 86;
  return 80;
}
export const fbIsTerminal = (s: FBStep|null) => !!s && (s.kind==="decision"||s.kind==="exit");
